import { isErpApiSuccess } from "../erpClient";
import { hasExistingPaymentLineById } from "./paymentIdempotency";
import {
  buildStandardPaymentLines,
  fingerprintStandardPaymentLine,
  mapApiPaymentsToUiRows,
  resolvePaymentBankAccountId,
} from "./paymentLinesErpAdapter";

export const isCustomOrdersApiSuccess = (res) => isErpApiSuccess(res);

/** Extract rows from custom orders list API (`data.items` or legacy shapes). */
export function extractCustomOrderListRows(res) {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (!d || typeof d !== "object") return [];
  if (Array.isArray(d.items)) return d.items;
  if (Array.isArray(d.custom_order_details)) return d.custom_order_details;
  if (Array.isArray(d.custom_orders)) return d.custom_orders;
  if (Array.isArray(d.records)) return d.records;
  if (Array.isArray(d.data?.custom_order_details)) return d.data.custom_order_details;
  if (d.id != null || d.doc_number || d.co_number) return [d];
  return [];
}

function pickFirstRow(value) {
  if (Array.isArray(value)) return value[0] || null;
  if (value && typeof value === "object") return value;
  return null;
}

/** Pick the order document from modular showCustomOrder envelopes (array or object). */
export function pickCustomOrderRowFromResponse(res) {
  const data = res?.data;
  if (data == null) return null;

  if (Array.isArray(data)) return data[0] || null;

  const nested = pickFirstRow(data.data);
  if (nested?.custom_order_entry || nested?.id != null) return nested;

  const fromDetails = pickFirstRow(data.custom_order_details);
  if (fromDetails) return fromDetails;

  if (data.custom_order_entry || data.id != null) return data;

  return nested;
}

function collectPaymentsFromShowResponse(res, row) {
  const data = res?.data;
  const candidates = [
    row?.payments,
    row?.payment_details,
    !Array.isArray(data) ? data?.payments : null,
    !Array.isArray(data) ? data?.payment_details : null,
    !Array.isArray(data) ? data?.custom_order_details?.payments : null,
    !Array.isArray(data) ? data?.custom_order_details?.payment_details : null,
    res?.payments,
    res?.payment_details,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate;
    }
  }

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  return [];
}

/**
 * Unwrap showCustomOrder response and merge `payments` when they sit on the
 * envelope instead of inside `custom_order_details`.
 */
export function resolveCustomOrderShowPayload(res) {
  const row = pickCustomOrderRowFromResponse(res);
  const payments = collectPaymentsFromShowResponse(res, row);
  const base = row && typeof row === "object" ? row : {};
  return { ...base, payments };
}

/** Map GET /custom_orders show payments to EditCustomOrderEntry payment row state. */
export function mapShowPaymentsToCustomOrderPaymentRows(payments) {
  return mapApiPaymentsToUiRows(payments);
}

export function createCustomOrderIdempotencyKey() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function createStableIdempotencyKeyFromPayload(payload) {
  // Not cryptographic - just used as a stable fallback fingerprint.
  const json = JSON.stringify(payload ?? {});
  return `fp-${Date.now()}-${hashString(json)}`;
}

function hashString(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h).toString(16);
}

export function sumPaymentAmounts(payments) {
  if (!Array.isArray(payments)) return 0;
  return payments.reduce((s, p) => {
    const v =
      p?.amount ??
      p?.amt ??
      p?.total_amount ??
      p?.totalAmount ??
      0;
    const n = parseFloat(v);
    return s + (Number.isFinite(n) ? n : 0);
  }, 0);
}

/** @deprecated Use `resolvePaymentBankAccountId` — kept for existing imports. */
export { resolvePaymentBankAccountId as resolveCustomOrderPaymentBankType } from "./paymentLinesErpAdapter";

/**
 * Build standard `payments[]` for custom order update multipart submit.
 */
export function buildCustomOrderUpdatePaymentLines(rows, options = {}) {
  const {
    paymentTypes = [],
    payModeLabels = {},
    createKey,
    persistKeys,
  } = options;

  return buildStandardPaymentLines(rows, {
    isUpdate: true,
    paymentTypes,
    payModeLabels,
    createKey,
    persistKeys,
  });
}

/** Stable fingerprint for one payment line in update payload (ignores idempotency_key). */
export function fingerprintCustomOrderPaymentSubmitLine(line = {}) {
  return fingerprintStandardPaymentLine(line);
}

export function fingerprintCustomOrderPaymentRow(row = {}, payModeLabel = "") {
  const paymentId = row.paymentId ?? row.payment_id;
  const hasExisting =
    row.isExistingPayment === true && hasExistingPaymentLineById(paymentId ?? row.id);
  const ledgerId = hasExisting ? (paymentId ?? row.id) : null;
  return fingerprintStandardPaymentLine({
    id: ledgerId,
    bank_account_id: resolvePaymentBankAccountId(row, []) || row.type || 0,
    payment_mode: payModeLabel,
    bank_name: row.bankDisplayName || "",
    remarks: row.remarks || "",
    amount: Number(row.amount) || 0,
  });
}

/** Snapshot loaded payment rows for change detection on edit submit. */
export function snapshotCustomOrderPaymentRows(rows, payModeLabels = {}) {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((row) => row.pay_mode && Number(row.amount) > 0)
    .map((row) =>
      fingerprintCustomOrderPaymentRow(
        row,
        payModeLabels[row.pay_mode] || row.pay_mode || "",
      ),
    )
    .sort();
}

/** True when submit payload differs from snapshot taken at load (or new payments added). */
export function haveCustomOrderPaymentsChanged(
  submitLines,
  loadedFingerprints,
) {
  const current = (Array.isArray(submitLines) ? submitLines : [])
    .map(fingerprintCustomOrderPaymentSubmitLine)
    .sort();
  const loaded = Array.isArray(loadedFingerprints)
    ? [...loadedFingerprints].sort()
    : null;
  if (loaded === null) return current.length > 0;
  if (current.length !== loaded.length) return true;
  return current.some((fp, idx) => fp !== loaded[idx]);
}

